import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { CurrentMetricOverview } from './CurrentMetricOverview';
import { useSelector } from 'react-redux';
import { metricsHelper } from './helpers/MetricsHelper';

const DashboardMetrics: React.FC = () => {
    const [diagnosticsAwaiting, setDiagnosticsAwaiting] = useState(0);
    const [diagnosticsAwaitingCaseCount, setDiagnosticsAwaitingCaseCount] =
        useState(0);
    const [diagnostics, setDiagnostics] = useState(0);
    const [diagnosticsCaseCount, setDiagnosticsCaseCount] = useState(0);
    const [treatmentPlanning, setTreatmentPlanning] = useState(0);
    const [treatmentPlanningCaseCount, setTreatmentPlanningCaseCount] =
        useState(0);
    const [treatmentAwaiting, setTreatmentAwaiting] = useState(0);
    const [treatmentAwaitingCaseCount, setTreatmentAwaitingCaseCount] =
        useState(0);
    const [underTreatment, setUnderTreatment] = useState(0);
    const [underTreatmentCaseCount, setUnderTreatmentCaseCount] = useState(0);
    const [underTreatmentProcedure, setUnderTreatmentProcedure] = useState(0);
    const [
        underTreatmentprocedureCaseCount,
        setUnderTreatmentProcedureCaseCount,
    ] = useState(0);
    const [underDrugTreatment, setUnderDrugTreatment] = useState(0);
    const [underDrugTreatmentCaseCount, setUnderDrugTreatmentCaseCount] =
        useState(0);
    const [totalPostTreatmentCaseCount, setTotalPostTreatmentCaseCount] =
        useState(0);
    const metrics = useSelector(
        (state: any) => state.dashboard && state.dashboard.metrics
    );

    const aggregateCurrentData = (data: any, count: number) => {
        let percentage = null;
        const formattedData = Array.isArray(data) ? data : [data];
        const totalCount = formattedData.reduce(
            (total, item) => total + (item.COUNT || 0),
            0
        );
        const currentTimestamp = Date.now();
        const daysInMs = count * 24 * 60 * 60 * 1000;
        const countLessThanCountDays = formattedData.reduce((count, item) => {
            // console.log('ITEM', new Date(item.registrationDate))
            const differenceInMs = Math.abs(
                currentTimestamp - item.registrationDate
            );

            // const days = differenceInMs / (1000 * 60 * 60 * 24);
            // console.log('DIFFERENCE', days)

            return differenceInMs < daysInMs
                ? count + (item.COUNT || 0)
                : count;
        }, 0);
        if (totalCount) {
            percentage = (countLessThanCountDays / totalCount) * 100;
        }

        return {
            totalCount,
            percentage,
        };
    };

    const aggregateTreatmentData = (
        data: any,
        itemName: string,
        count: number
    ) => {
        const formattedData = Array.isArray(data) ? data : [data];
        const totalCount = formattedData.reduce((sum, item) => {
            return item._id !== null ? sum + (item.COUNT || 0) : sum;
        }, 0);
        const countUnderThreshold = formattedData.reduce((sum, item) => {
            return item._id !== null && item[itemName] < count
                ? sum + (item.COUNT || 0)
                : sum;
        }, 0);
        const percentage = totalCount
            ? (countUnderThreshold / totalCount) * 100
            : 0;

        return {
            totalCount,
            percentage,
        };
    };

    useEffect(() => {
        if (
            metrics &&
            metrics.totalDiagnosticsAwaiting &&
            metrics.totalDiagnosticsAwaiting.items &&
            metrics.totalPostTreatment.items.length
        ) {
            const diagAwaitPercentage = aggregateCurrentData(
                metrics.totalDiagnosticsAwaiting.items,
                15
            );
            setDiagnosticsAwaiting(diagAwaitPercentage.percentage);
            setDiagnosticsAwaitingCaseCount(diagAwaitPercentage.totalCount);
        }
        if (
            metrics &&
            metrics.totalDiagnostics &&
            metrics.totalDiagnostics.items &&
            metrics.totalPostTreatment.items.length
        ) {
            const diagPercentage = aggregateCurrentData(
                metrics.totalDiagnostics.items,
                29
            );
            setDiagnostics(diagPercentage.percentage);
            setDiagnosticsCaseCount(diagPercentage.totalCount);
        }
        if (
            metrics &&
            metrics.totalTreatmentPlanning &&
            metrics.totalTreatmentPlanning.items &&
            metrics.totalPostTreatment.items.length
        ) {
            const treatmentPlanningPercentage = aggregateCurrentData(
                metrics.totalTreatmentPlanning.items,
                32
            );
            setTreatmentPlanning(treatmentPlanningPercentage.percentage);
            setTreatmentPlanningCaseCount(
                treatmentPlanningPercentage.totalCount
            );
        }
        if (
            metrics &&
            metrics.totalTreatmentAwaiting &&
            metrics.totalTreatmentAwaiting.items &&
            metrics.totalPostTreatment.items.length
        ) {
            const treatmentAwaitingPercentage = aggregateCurrentData(
                metrics.totalTreatmentAwaiting.items,
                63
            );
            setTreatmentAwaiting(treatmentAwaitingPercentage.percentage);
            setTreatmentAwaitingCaseCount(
                treatmentAwaitingPercentage.totalCount
            );
        }
        if (
            metrics &&
            metrics.totalUnderTreatment &&
            metrics.totalUnderTreatment.items &&
            metrics.totalPostTreatment.items.length
        ) {
            const underTreatmentPercentage = aggregateTreatmentData(
                metrics.totalUnderTreatment.items,
                'calculateUnderTreatment',
                63
            );
            setUnderTreatment(underTreatmentPercentage.percentage);
            setUnderTreatmentCaseCount(underTreatmentPercentage.totalCount);
        }
        if (
            metrics &&
            metrics.totalUnderTreatmentProcedure &&
            metrics.totalUnderTreatmentProcedure.items &&
            metrics.totalPostTreatment.items.length
        ) {
            const underTreatmentProcedurePercentage = aggregateTreatmentData(
                metrics.totalUnderTreatmentProcedure.items,
                'calculateUnderTreatmentProcedure',
                63
            );
            setUnderTreatmentProcedure(
                underTreatmentProcedurePercentage.percentage
            );
            setUnderTreatmentProcedureCaseCount(
                underTreatmentProcedurePercentage.totalCount
            );
        }
        if (
            metrics &&
            metrics.totalUnderDrugTreatment &&
            metrics.totalUnderDrugTreatment.items &&
            metrics.totalPostTreatment.items.length
        ) {
            const underDrugTreatmentPercentage = aggregateTreatmentData(
                metrics.totalUnderDrugTreatment.items,
                'calculateUnderDrugTreatment',
                63
            );
            setUnderDrugTreatment(underDrugTreatmentPercentage.percentage);
            setUnderDrugTreatmentCaseCount(
                underDrugTreatmentPercentage.totalCount
            );
        }
        if (
            metrics &&
            metrics.totalPostTreatment &&
            metrics.totalPostTreatment.items &&
            metrics.totalPostTreatment.items.length
        ) {
            setTotalPostTreatmentCaseCount(
                metrics.totalPostTreatment.items[0].COUNT
            );
        }
    }, [metrics]);

    return (
        <Grid container spacing={3}>
            {metricsHelper(
                diagnosticsAwaiting,
                diagnostics,
                diagnosticsAwaitingCaseCount,
                diagnosticsCaseCount,
                treatmentPlanning,
                treatmentAwaiting,
                treatmentPlanningCaseCount,
                treatmentAwaitingCaseCount,
                underTreatment,
                underTreatmentCaseCount,
                underTreatmentProcedure,
                underTreatmentprocedureCaseCount,
                underDrugTreatment,
                underDrugTreatmentCaseCount,
                totalPostTreatmentCaseCount
            )
                .reduce((acc, metric, i) => {
                    // Group every two metrics together for each row
                    if (i % 2 === 0) acc.push([]);
                    acc[acc.length - 1].push(metric);
                    return acc;
                }, [])
                .map((groupRow, rowIndex) => (
                    <Grid
                        container
                        item
                        xs={12}
                        spacing={8}
                        key={`metric-row-${rowIndex}`}
                    >
                        {groupRow.map((metric, groupIndex) => (
                            <Grid
                                item
                                xs={
                                    metric.group === 'Treatment'
                                        ? 9
                                        : metric.group === 'Post-Treatment'
                                        ? 3
                                        : 6
                                } // Treatment takes 9 columns, Post-Treatment 3 columns, others 6 columns
                                key={`metrics-group-${groupIndex}`}
                            >
                                {/* Group Title */}
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 'bold', mb: 2 }}
                                >
                                    {metric.group}
                                </Typography>
                                <Grid container spacing={3}>
                                    {metric.metrics.map((metricItem, j) => (
                                        <Grid
                                            item
                                            xs={
                                                metric.group === 'Treatment' &&
                                                metric.metrics.length === 3
                                                    ? 4 // Each card in Treatment takes 4 columns (3 cards in 9 columns)
                                                    : metric.group ===
                                                          'Post-Treatment' &&
                                                      metric.metrics.length ===
                                                          1
                                                    ? 12 // Single card in Post-Treatment takes full width within its 3 columns
                                                    : 6 // For Investigation and Pre-Treatment (2 cards in 6 columns)
                                            }
                                            key={`metrics-${j}`}
                                        >
                                            <CurrentMetricOverview
                                                metrics={metrics}
                                                metric={metricItem}
                                                title={metricItem.title}
                                                subtitle={
                                                    metricItem.currentMetricsSubtitle
                                                }
                                                secondaryTitle={
                                                    metricItem.currentMetricsTitle
                                                }
                                                percentage={
                                                    metricItem.currentMetricsPercentage
                                                }
                                                target={
                                                    metricItem.currentMetricsTarget
                                                }
                                                caseCount={
                                                    metricItem.currentMetricCaseCount
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                ))}
        </Grid>
    );
};

export default DashboardMetrics;
